import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Code2, ArrowRight, Briefcase, Wrench, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

export const ServicesHTAG = () => {
  const services = [
    {
      icon: Briefcase,
      title: 'Accompagnement',
      description: 'Accompagnement des équipes, transformation digitale, process optimisés et conduite du changement.',
      gradient: 'apple-gradient-purple',
      link: '/services/accompagnement'
    },
    {
      icon: Code2,
      title: 'Design & web sur mesure',
      description: 'Création/refonte de sites, identité visuelle, supports digitaux & print, stratégie de communication.',
      gradient: 'apple-gradient-blue',
      link: '/services/design-web'
    },
    {
      icon: Wrench,
      title: 'Dépannage informatique',
      description: 'Support IT pour postes de travail et maintenance. Priorité à la continuité d\'activité et productivité des équipes.',
      gradient: 'apple-gradient-cyan',
      link: '/services/depannage'
    },
    {
      icon: Users,
      title: 'Coaching digital',
      description: 'Formations et coaching d\'équipe pour monter en compétence sur les outils numériques.',
      gradient: 'from-green-500 to-emerald-500',
      link: '/services/coaching'
    },
  ];

  return (
    <section className="apple-section px-6 lg:px-8 relative overflow-hidden">
      {/* Background gradient orb */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-10 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(0, 113, 227, 0.3) 0%, transparent 70%)',
          filter: 'blur(100px)'
        }}
      />

      <div className="container mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl lg:text-6xl font-bold mb-6" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #e5e5e7 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 700,
            letterSpacing: '-0.03em'
          }}>
            Nos Services
          </h2>
          <p className="apple-subtitle max-w-2xl mx-auto">
            Solutions complètes d'assistance numérique, IA et accompagnement digital
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <Link
                key={index}
                to={service.link}
              >
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ 
                    delay: index * 0.1, 
                    duration: 0.6,
                    ease: [0.25, 0.46, 0.45, 0.94]
                  }}
                  whileHover={{ 
                    y: -12,
                    transition: { duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }
                  }}
                  className="apple-card group p-8 h-full cursor-pointer flex flex-col"
                >
                  {/* Icon with gradient */}
                  <div className={`w-16 h-16 rounded-2xl ${service.gradient} p-4 mb-6 flex items-center justify-center relative overflow-hidden`}>
                    <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent" />
                    <Icon className="w-8 h-8 text-white relative z-10" strokeWidth={1.5} />
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-2xl font-semibold mb-4 transition-colors duration-300" style={{
                    color: '#f5f5f7',
                    letterSpacing: '-0.02em'
                  }}>
                    {service.title}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-gray-400 mb-6 flex-grow leading-relaxed" style={{
                    fontSize: '15px',
                    lineHeight: '1.6'
                  }}>
                    {service.description}
                  </p>

                  {/* CTA Link */}
                  <div className="mt-auto inline-flex items-center gap-2 text-sm font-medium group-hover:gap-3 transition-all duration-300"
                    style={{ color: '#0071e3' }}
                  >
                    <span>En savoir plus</span>
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </div>

                  {/* Subtle hover glow effect */}
                  <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{
                      background: 'radial-gradient(circle at 50% 0%, rgba(0, 113, 227, 0.05) 0%, transparent 70%)'
                    }}
                  />
                </motion.div>
              </Link>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center mt-16"
        >
          <p className="text-gray-400 mb-6">Besoin d'une solution personnalisée ?</p>
          <button className="apple-button">
            Contactez-nous
          </button>
        </motion.div>
      </div>

      {/* Apple divider */}
      <div className="apple-divider mt-24 max-w-6xl mx-auto" />
    </section>
  );
};

export default ServicesHTAG;
